/**
 * src/types/charts/scatter.chart.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Scatter / bubble chart — X/Y numeric coordinates with optional bubble radius
 * and grouping. `showTrendLine: true` computes a linear regression line.
 */

import type { HexColor } from '../core/primitives';
import type { ChartDataPoint } from '../core/interfaces.shared';
import {
  makeTitle, makeSubtitle,
  DEFAULT_BACKGROUND, DEFAULT_LEGEND, DEFAULT_DATA_LABELS,
  DEFAULT_AXIS, DEFAULT_Y_AXIS,
} from '../core/defaults.shared';
import type { ChartConfigBase } from '../core/chart-config.base';
import type { SchemaNote } from '../chart.schema';

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATION STYLES
// ─────────────────────────────────────────────────────────────────────────────

export type ScatterAnimationStyle =
  | 'fadeIn' | 'popIn' | 'sequential' | 'wave' | 'ripple' | 'drift'
  | 'clusterReveal';

export const SCATTER_ANIMATION_STYLES: ScatterAnimationStyle[] = [
  'fadeIn', 'popIn', 'sequential', 'wave', 'ripple', 'drift',
  'clusterReveal',
];

export const SCATTER_ELEMENT_PANEL_STYLES: ScatterAnimationStyle[] = SCATTER_ANIMATION_STYLES;


// ─────────────────────────────────────────────────────────────────────────────
// DATA POINT
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Scatter / bubble plot data point.
 * Both `x` and `y` are numeric (not categories).
 * `radius` enables bubble mode — point drawn at radius px instead of global
 * spacing.pointSize. Omit or set to 0 to use global point size.
 * `group` — points with the same group share a color entry in the legend.
 */
export interface ScatterDataPoint extends ChartDataPoint {
  chartType:     'scatter';
  x:             number;     // x-axis value (numeric)
  y:             number;     // y-axis value (numeric)
  radius?:       number;     // optional bubble radius px. 0 = use global pointSize.
  group?:        string;     // optional group key for legend + color sharing
  label?:        string;     // optional label text rendered near the point
  labelOffsetX?: number;     // per-point label horizontal nudge px. Default: 0.
  labelOffsetY?: number;     // per-point label vertical nudge px. Default: 0.
}


// ─────────────────────────────────────────────────────────────────────────────
// SPACING CONFIG
// ─────────────────────────────────────────────────────────────────────────────

export interface ScatterSpacingConfig {
  chartType:         'scatter';
  pointSize:         number;    // base marker radius px. Range: 2–60. Default: 12.
  showTrendLine:     boolean;   // draw linear regression trend line. Default: false.
  trendLineColor:    HexColor;  // trend line color. Default: '#94a3b8'.
  trendLineWidth:    number;    // trend line stroke width px. Range: 1–8. Default: 2.
  showRSquared:      boolean;   // show R² label beside trend line. Default: true.
  // Axis title labels
  xAxisLabel:        string;    // title below X axis ticks. Default: ''.
  yAxisLabel:        string;    // title left of Y axis ticks (rotated). Default: ''.
  // Point label display mode
  pointLabelMode:    'name' | 'xy' | 'value-y' | 'none';  // Default: 'name'.
  // Quadrant reference lines
  showQuadrantLines: boolean;   // draw X/Y divider lines. Default: false.
  quadrantX:         number;    // X value for vertical divider. Default: 50.
  quadrantY:         number;    // Y value for horizontal divider. Default: 50.
  // Point appearance
  pointSpacing:      number;    // reserved for future jitter implementation. Default: 0.
  pointOpacity:      number;    // base fill opacity 0–1 for all points. Default: 0.75.
  axisPadding:       number;    // padding multiplier around data range. 0 = tight, 0.1 = default 10%, 0.3 = generous. Range: 0–0.5.
}


// ─────────────────────────────────────────────────────────────────────────────
// DEFAULTS
// ─────────────────────────────────────────────────────────────────────────────

export const SCATTER_DEFAULTS: ChartConfigBase<ScatterSpacingConfig> = {
  chartType: 'scatter',
  content: {
    titles:    [makeTitle('Correlation Analysis')],
    subtitles: [makeSubtitle('X vs Y Distribution')],
    infoBoxes: [],
  },
  animation:  { duration: 5, style: 'popIn', textSpeed: 1 },
  background: { ...DEFAULT_BACKGROUND },
  layout: {
    chartPosition: { x: 0, y: 0 },
    chartMargin:   { top: 180, right: 80, bottom: 130, left: 130 },
  },
  axes: {
    x: { ...DEFAULT_AXIS,   showGridLines: true },
    y: { ...DEFAULT_Y_AXIS, showGridLines: true },
  },
  legend:     { ...DEFAULT_LEGEND, visible: false },
  dataLabels: { ...DEFAULT_DATA_LABELS, visible: true },
  spacing: {
    chartType:      'scatter',
    pointSize:      12,
    showTrendLine:  false,
    trendLineColor: '#94a3b8',
    trendLineWidth: 2,
    showRSquared:      true,
    xAxisLabel:        '',
    yAxisLabel:        '',
    pointLabelMode:    'name',
    showQuadrantLines: false,
    quadrantX:         50,
    quadrantY:         50,
    pointSpacing:      0,
    pointOpacity:      0.75,
    axisPadding:       0.1,
  },
  export: { quality: '1080p', isLooping: true, canvasFormat: '16:9' },
};


// ─────────────────────────────────────────────────────────────────────────────
// SCHEMA NOTES — consumed by chart.schema.ts merger and getChartSchemaSlice()
// ─────────────────────────────────────────────────────────────────────────────

export const SCATTER_SCHEMA_NOTES: Record<string, SchemaNote> = {

  'scatter.pointSize': {
    description: 'Base marker radius in canvas pixels. Individual points can override via the radius field (bubble mode).',
    type:    'number',
    range:   '2–40',
    default: 12,
    unit:    'canvas pixels',
    affects: ['default marker size for all points'],
    bounds:  ['Min 2. Max 40.'],
    synergizes: [],
    tensions:   ['data density — large points on dense scatter plots cause overlap'],
    agentNote:  "6–14 for standard scatter. 20+ for sparse data. Points with radius field override this. For bubble charts, set pointSize small (6) and use per-point radius for sizing.",
  },

  'scatter.showTrendLine': {
    description: 'When true, draws a dashed linear regression trend line across the chart area.',
    type:    'boolean',
    range:   'true | false',
    default: false,
    unit:    'boolean',
    affects: ['trend line visibility', 'R² label visibility'],
    bounds:  ['Requires at least 2 visible data points.'],
    synergizes: ['scatter.trendLineColor', 'scatter.trendLineWidth', 'scatter.showRSquared'],
    tensions:   [],
    agentNote:  "Enable for correlation analysis. The trend line is always linear (OLS). R² label shows fit quality. Disable for pure distribution visualizations.",
  },

  'scatter.trendLineColor': {
    description: 'Color of the trend line stroke.',
    type:    'HexColor',
    range:   'any valid hex color',
    default: '"#94a3b8"',
    unit:    'hex',
    affects: ['trend line color', 'R² label color'],
    bounds:  [],
    synergizes: ['scatter.showTrendLine'],
    tensions:   [],
    agentNote:  "Default grey (#94a3b8) is subtle — good for not competing with data points. Use a darker color on white backgrounds.",
  },

  'scatter.trendLineWidth': {
    description: 'Stroke width of the trend line in canvas pixels.',
    type:    'number',
    range:   '1–8',
    default: 2,
    unit:    'canvas pixels',
    affects: ['trend line visual weight'],
    bounds:  ['Min 1. Max 8.'],
    synergizes: ['scatter.showTrendLine'],
    tensions:   [],
    agentNote:  "1–2 for subtle reference lines. 3–4 for emphasis. Above 5 the line starts competing with the data points.",
  },

  'scatter.showRSquared': {
    description: 'When true, shows the R² coefficient of determination label near the trend line.',
    type:    'boolean',
    range:   'true | false',
    default: true,
    unit:    'boolean',
    affects: ['R² label visibility'],
    bounds:  ['Only shown when showTrendLine is also true.'],
    synergizes: ['scatter.showTrendLine'],
    tensions:   [],
    agentNote:  "R² near 1.0 = strong linear correlation. Near 0 = no linear relationship. The label appears at the right end of the trend line inside the chart area.",
  },

  'scatter.xAxisLabel': {
    description: 'Title label displayed below the X axis tick labels.',
    type:    'string',
    range:   'any string',
    default: '""',
    unit:    'string',
    affects: ['X axis title label'],
    bounds:  [],
    synergizes: ['scatter.yAxisLabel'],
    tensions:   [],
    agentNote:  "Always set for scatter charts — without it the X axis numbers have no context. Example: 'NPS Score', 'Revenue ($M)', 'Age'.",
  },

  'scatter.yAxisLabel': {
    description: 'Title label displayed left of the Y axis tick labels (rotated 90°).',
    type:    'string',
    range:   'any string',
    default: '""',
    unit:    'string',
    affects: ['Y axis title label'],
    bounds:  [],
    synergizes: ['scatter.xAxisLabel'],
    tensions:   [],
    agentNote:  "Always set for scatter charts. Example: 'Revenue ($K)', 'Growth Rate (%)', 'Score'.",
  },

  'scatter.pointLabelMode': {
    description: "What text to show near each data point. 'name' = entity name (most useful). 'xy' = coordinate pair. 'value-y' = Y value only. 'none' = no labels.",
    type:    "'name' | 'xy' | 'value-y' | 'none'",
    range:   "'name' | 'xy' | 'value-y' | 'none'",
    default: "'name'",
    unit:    'enum',
    affects: ['per-point label content'],
    bounds:  [],
    synergizes: ['dataLabels.visible — must be true for labels to render'],
    tensions:   ['data density — name labels overlap on dense scatter plots; use none for 20+ points'],
    agentNote:  "'name' is correct for competitive analysis, bubble charts, and most scatter use cases. Use 'xy' only for pure mathematical plots. Use 'none' for dense data where labels would overlap.",
  },

  'scatter.showQuadrantLines': {
    description: 'Draw X and Y reference lines dividing the chart into four quadrants (BCG matrix, Magic Quadrant style).',
    type:    'boolean',
    range:   'true | false',
    default: false,
    unit:    'boolean',
    affects: ['quadrant divider lines'],
    bounds:  [],
    synergizes: ['scatter.quadrantX', 'scatter.quadrantY'],
    tensions:   [],
    agentNote:  "Enable for 2×2 matrix visualizations. Set quadrantX and quadrantY to meaningful midpoints (e.g. median X, median Y).",
  },

  'scatter.quadrantX': {
    description: 'X-axis value where the vertical quadrant divider line is drawn.',
    type:    'number',
    range:   'any number within the X data range',
    default: 50,
    unit:    'data units (same as X axis)',
    affects: ['vertical quadrant line position'],
    bounds:  [],
    synergizes: ['scatter.showQuadrantLines'],
    tensions:   [],
    agentNote:  "Set to the median X value for balanced quadrants. Or set to a meaningful threshold (e.g. 'break-even point' on the X axis).",
  },

  'scatter.quadrantY': {
    description: 'Y-axis value where the horizontal quadrant divider line is drawn.',
    type:    'number',
    range:   'any number within the Y data range',
    default: 50,
    unit:    'data units (same as Y axis)',
    affects: ['horizontal quadrant line position'],
    bounds:  [],
    synergizes: ['scatter.showQuadrantLines'],
    tensions:   [],
    agentNote:  "Set to the median Y value for balanced quadrants.",
  },

  'scatter.pointSpacing': {
    description: 'Minimum pixel gap between overlapping points. 0 = no jitter.',
    type:    'number',
    range:   '0–200',
    default: 0,
    unit:    'canvas pixels',
    affects: ['point overlap'],
    bounds:  ['Min 0. Max 200.'],
    synergizes: [],
    tensions:   ['high values spread points away from true positions'],
    agentNote:  '0 for accurate positioning. 10–30 if points overlap heavily.',
  },

  'scatter.pointOpacity': {
    description: 'Base fill opacity for all points. Per-point styleOverride.fillOpacity multiplies with this.',
    type:    'number',
    range:   '0–1',
    default: 0.75,
    unit:    'fraction',
    affects: ['point transparency'],
    bounds:  ['Min 0. Max 1.'],
    synergizes: ['styleOverride.fillOpacity — multiplied together'],
    tensions:   ['low values make points hard to see on light backgrounds'],
    agentNote:  '0.75 is the established default. Use 0.5 for dense overlapping data. Use 1.0 for sparse data.',
  },

  'scatter.axisPadding': {
    description: 'Padding multiplier applied to both X and Y data ranges. Controls breathing room around data points — the scatter equivalent of bar gap.',
    type:    'number',
    range:   '0–0.5',
    default: 0.1,
    unit:    'fraction of data range',
    affects: ['chart area whitespace', 'point density appearance'],
    bounds:  ['Min 0. Max 0.5.'],
    synergizes: ['scatter.pointSize — larger points need more padding to avoid edge clipping'],
    tensions:   ['0 looks cramped; above 0.3 wastes chart area'],
    agentNote:  '0.1 is the default (10% padding). Use 0.05 for dense data. Use 0.2–0.3 for sparse data with large points.',
  },

  'dataPoint.scatter.x': {
    description: 'X-axis numeric value for this scatter point. Both axes are numeric — unlike bar/line where X is categorical.',
    type:    'number',
    range:   'any number',
    default: null,
    unit:    'arbitrary numeric unit',
    affects: ['point horizontal position', 'X axis tick range'],
    bounds:  [],
    synergizes: ['dataPoint.scatter.y — together determine point position'],
    tensions:   [],
    agentNote:  "X axis range is auto-computed with 10% padding. Points are NOT connected in any order — scatter has no lines between points.",
  },

  'dataPoint.scatter.y': {
    description: 'Y-axis numeric value for this scatter point.',
    type:    'number',
    range:   'any number',
    default: null,
    unit:    'arbitrary numeric unit',
    affects: ['point vertical position', 'Y axis tick range', 'trend line slope'],
    bounds:  [],
    synergizes: ['dataPoint.scatter.x'],
    tensions:   [],
    agentNote:  "Y axis range uses resolveYAxisRange with minValue/maxValue from axes.y config. Negative values are supported.",
  },

};
